import { useEffect, useCallback } from 'react';
import {
  Datagrid,
  List,
  TextField,
  DateField,
  SelectInput,
  useListController,
  FunctionField,
  TextInput,
  useRedirect,
  TopToolbar,
  Button
} from 'react-admin';
import { LABELS } from 'shared/constants/labels';

const DomainChoices = [
  { id: 'isB2B', name: 'B2B' },
  { id: 'named', name: 'Именованные промокоды' },
  { id: 'private', name: 'Личные промокоды' }
];

const TypeChoices = [
  { id: 'amount', name: 'С номиналом' },
  { id: 'percentage', name: 'С процентом' }
];

const UsedChoices = [
  { id: 'used', name: 'Использованные' },
  { id: 'unused', name: 'Не использованные' },
  { id: 'expired', name: 'Истекшие' }
];

const SearchTermChoices = [
  { id: 'companyName', name: 'Имя компании' },
  { id: 'consumerEmail', name: 'Email пользователя' },
  { id: 'consumerName', name: 'Имя пользователя' },
  { id: 'code', name: 'Название промокода' },
  { id: 'adminName', name: 'Имя создателя промокода' },
];

const validateSearchValue = (value: string | null | undefined) => {
  if (!value || value.trim() === '') {
    return undefined;
  }
  
  if (!/^[а-яА-Яa-zA-Z0-9]/.test(value)) {
    return 'Строка должна начинаться с буквы или цифры';
  }
  
  if (!/^[а-яА-Яa-zA-Z0-9\s.,@+]*$/.test(value)) {
    return 'Можно использовать только буквы, цифры, пробелы и символы .,@+';
  }
  
  return undefined;
};

const parseSearchValue = (value: string | null | undefined) => {
  if (!value) return '';
  
  return value
    .replace(/[^а-яА-Яa-zA-Z\d.,@+\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const promocodeFilters = [
  <TextInput
    key="searchValue"
    label="Строка для поиска"
    placeholder="Строка для поиска"
    source="searchValue"
    parse={parseSearchValue}
    validate={validateSearchValue}
    alwaysOn
  />,
  <SelectInput
    key="searchTerm"
    label="Искать по:"
    source="searchTerm"
    choices={SearchTermChoices}
    alwaysOn
  />,
  <SelectInput
    key="promocodeDomain"
    label="Принадлежность"
    source="booleanFilter.promocodeDomain"
    choices={DomainChoices}
    emptyText="Все"
    alwaysOn
  />,
  <SelectInput
    key="promocodeType"
    label="Тип"
    source="booleanFilter.promocodeType"
    choices={TypeChoices}
    emptyText="Все"
    alwaysOn
  />,
  <SelectInput
    key="promocodeStatus"
    label="Статус"
    source="booleanFilter.promocodeStatus"
    choices={UsedChoices}
    emptyText="Все"
    alwaysOn
  />
];

const ListActions = () => {
  const redirect = useRedirect();

  const handleCreateNamedPromocode = useCallback(() => {
    redirect('/promocodes/create');
  }, [redirect]);

  return (
    <TopToolbar>
      <Button
        onClick={handleCreateNamedPromocode}
        label="Создать именной промокод"
        color="secondary"
      />
    </TopToolbar>
  );
};

const renderDiscountType = (record: { discountType?: string }) => {
  if (!record?.discountType) return '';
  
  const discountTypeMap: Record<string, string> = {
    'amount': 'номинал',
    'percentage': 'процент'
  };
  return discountTypeMap[record.discountType] || record.discountType;
};

export const PromocodesList = () => {
  const { setFilters } = useListController();

  useEffect(() => {
    setFilters({
      'booleanFilter.promocodeDomain': '',
      'booleanFilter.promocodeType': '',
      'booleanFilter.promocodeStatus': ''
    }, {});
  }, []);

  return (
    <List filters={promocodeFilters} actions={<ListActions />}>
      <Datagrid bulkActionButtons={false} rowClick="show">
        <TextField
          source="code"
          label={LABELS.promocodeCode}
          sortable={false}
        />
        <FunctionField
          label={LABELS.promocodeType}
          render={renderDiscountType}
          sortable={false}
        />
        <TextField
          source="amount"
          label={LABELS.promocodeAmount}
          sortable={false}
        />
        <TextField
          source="percentage"
          label={LABELS.promocodePercentage}
          sortable={false}
        />
        <TextField
          source="timesUsed"
          label={LABELS.promocodeTimesUsed}
          sortable={false}
        />
        <DateField
          source="expireDate"
          label={LABELS.promocodeExpireDate}
          sortable={false}
          showTime
        />
        <DateField
          source="createdAt"
          label={LABELS.createdAt}
          sortable={false}
          showTime
        />
        <TextField
          source="companyName"
          label={LABELS.companyName}
          sortable={false}
        />
        <TextField
          source="consumerEmail"
          label={LABELS.promocodeConsumerEmail}
          sortable={false}
        />
        <TextField
          source="consumerName"
          label={LABELS.promocodeConsumerName}
          sortable={false}
        />
        <TextField
          source="adminName"
          label={LABELS.promocodeCreatorName}
          sortable={false}
        />
      </Datagrid>
    </List>
  );
};
